// src/components/files/EmptyState.tsx
import React from 'react';
import {
  Box,
  Typography,
  Button,
  alpha,
} from '@mui/material';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useTranslation } from 'react-i18next';

interface EmptyStateProps {
  icon?: React.ReactNode;
  title?: string;
  description?: string;
  showActions?: boolean;
  onCreateFolder?: () => void;
  onUpload?: () => void;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  showActions = true,
  onCreateFolder,
  onUpload,
}) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 400,
        textAlign: 'center',
        p: 4,
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          width: 120,
          height: 120,
          borderRadius: '50%',
          bgcolor: alpha('#2563eb', 0.08),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3,
        }}
      >
        {icon || <FolderOpenIcon sx={{ fontSize: 56, color: 'grey.400' }} />}
      </Box>

      {/* Title */}
      <Typography variant="h6" fontWeight={600} color="text.primary" sx={{ mb: 1 }}>
        {title || t('fileManager.empty.title', 'Ce dossier est vide')}
      </Typography>

      {/* Description */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ maxWidth: 400, mb: 4 }}
      >
        {description || t('fileManager.empty.description', 'Commencez par creer un dossier ou importer des fichiers')}
      </Typography>

      {/* Actions */}
      {showActions && (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<CreateNewFolderIcon />}
            onClick={onCreateFolder}
          >
            {t('fileManager.empty.createFolder', 'Creer un dossier')}
          </Button>
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={onUpload}
          >
            {t('fileManager.empty.uploadFiles', 'Importer des fichiers')}
          </Button>
        </Box>
      )}

      {/* Drag and drop hint */}
      {showActions && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          {t('fileManager.empty.dragHint', 'ou glissez-deposez vos fichiers ici')}
        </Typography>
      )}
    </Box>
  );
};

export default EmptyState;
